// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The wrist menu, drawn (M5-24, M5-25).
//
// A single textured quad placed once for quick tools or a larger normal menu. `wristMenu.ts` decides what the menu is
// doing; this decides what it looks like, and the split is worth keeping because the arming
// behaviour is a safety property with tests and the panel is a picture.
//
// **One canvas, redrawn only when the text changes.** A six-item list at 60–90 Hz redrawn every
// frame would spend a texture upload per frame on a panel that changes when a finger moves
// between items — which is a few times a session. The panel tracks what it last drew and skips
// the upload otherwise, which is the difference between free and 4 MB/s of PCIe traffic on a
// device that has neither to spare.
//
// **It is legible at 0.25 m and nowhere else.** A wrist panel is read at wrist distance, so the
// type is sized for that and the panel is small: 0.22 m square, about a postcard. It was a
// playing card at 0.16 until the list reached ten rows and a headset reported the rows being
// mis-pressed — see `PANEL`. It is only up while a palm is turned over, so "in the way of the
// machine" is a cost paid for a second at a time.

import {
  CanvasTexture,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three'
import {
  labelFor, headerFor, activityLabel, primaryItem, footerFor,
  PAGING,
  contextRows,
  unavailableReason,
  type MenuItem,
  type MenuPresentation,
  type MenuState,
  type MenuView,
} from '../xr/wristMenu'
import { TapGate, PhysicalPoke } from '../xr/build/tap'
import { COMPACT_MENU, menuLayout } from '../xr/menuLayout'

export interface WristPanelBuild {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  readonly textures: readonly CanvasTexture[]
  /** Redraw if anything changed, and show or hide the panel. Cheap to call every frame. */
  update: (view: MenuView, state: MenuState, highlighted: MenuItem | undefined, feedback?: 'accepted'|'release') => void
  /**
   * Which item a fingertip at this world point is over, or undefined.
   *
   * Uses the mesh's own world matrix rather than reconstructing the transform, because the panel
   * hangs off a wrist that moves and rotates and there is exactly one correct answer to "where is
   * this point on that quad" — Three already knows it.
   */
  itemAt: (x: number, y: number, z: number) => MenuItem | undefined
  /**
   * A fingertip, every frame it is there: the item it **tapped** this frame, or undefined.
   *
   * **A row is tapped, not pinched** (M13-05's rule, which the wrist rows had not been given). The
   * first pointer was "pinch while over a row, on the pinch's edge", and the second device day of
   * M13 said what `tap.ts`'s header had already recorded: *"a relaxed hand reads as pinched, so a
   * pinch is not a decision."* The `[xr] hands` line showed the pointing hand as `pinch fist` at
   * rest, so the edge the pointer waited for had already happened before the finger reached the
   * row — *"the buttons highlight when selected but do not do anything."* A tap is the one gesture
   * every hand already makes at every control on the cabinet; this is `TapGate` on the panel's own
   * face, +z toward the person, with the row decided where the finger arrived.
   */
  press: (x: number, y: number, z: number, now: number, moving?: boolean) => MenuItem | undefined
  /**
   * The row the last press went to, while the finger that pressed it is still pushed in — for a
   * row that repeats while held (the test page's Step, polish to M13-25). Nothing once the finger
   * comes back out, leaves the panel, or the panel goes away.
   */
  held: () => MenuItem | undefined
  /**
   * Where the last press landed, for the console (13 Sep 2026): the row's index and count, and
   * how far down the row the finger arrived, 0 at its top edge and 1 at its bottom. Two device
   * sessions read presses one row off the row the author was aiming at, both toward the panel's
   * middle, and nothing said whether they landed near an edge or in the middle of the wrong row.
   * When the press went to the row the highlight had just left (`COMMIT_SECONDS`), `row` is that
   * row, `landed` is the row the finger actually arrived on — `down` is measured on it — and
   * `after` is how long after the highlight moved the finger arrived. Cleared by the read.
   */
  takeLastPress: () => LastPress | undefined
  /**
   * The hover that just ended without a press, for the console (13 Sep 2026, 23:55): which row
   * the finger left, and how near the picture it came while over it — so a row that lit and did
   * nothing can be told from a finger that only swept past. Cleared by the read.
   */
  takeLastLeave: () => LastLeave | undefined
  /** The pointing hand went away or was reassigned: let go of any press it was making. */
  release: () => void
  dispose: () => void
}

/** See `WristPanelBuild.takeLastLeave`. `nearest` is the smallest z seen, metres, +z in front. */
export interface LastLeave {
  readonly row: number
  readonly rows: number
  readonly nearest: number
}

/** See `WristPanelBuild.takeLastPress`. */
export interface LastPress {
  readonly row: number
  readonly rows: number
  readonly down: number
  readonly landed?: number
  readonly after?: number
}

/**
 * How long a row has to have been under the finger for a press to be *its* press, seconds.
 *
 * The 23:14 session on 13 Sep 2026 read two of eleven presses one row off the row the person
 * meant, and the console had the reason in the lines before each: the highlight had crossed from
 * the row they were aiming at to its neighbour 0.12 s and 0.04 s before the press, and the press
 * landed 99% and 17% of the way down the neighbour — a few millimetres over the boundary. A
 * fingertip drifts while it pokes. The row they meant is the one that was lit when they
 * committed, so a press that comes this soon after the highlight has moved from one row to the
 * next goes to the row it left — provided the row it left had been lit for at least this long,
 * so that a finger which flickered across a boundary and back, or swept in across a row from off
 * the panel, is pressing where it landed. Every deliberate move between rows in that session
 * took over 0.4 s.
 */
export const COMMIT_SECONDS = 0.2

/**
 * How close to the panel's plane a fingertip must be to be pointing at a row, metres.
 *
 * 0.035 — a finger's width either side. Generous, because the panel is a picture with no
 * thickness and a player cannot feel where it is; tight enough that a hand hanging at the wrist
 * is not silently hovering the bottom item.
 */
const TOUCH_DEPTH = 0.035

/**
 * Panel size in metres, and the pixel canvas behind it.
 *
 * **It grew with the list rather than squeezing to fit it** (M8-09). Two mute rows on the old
 * 0.13 m panel would have taken every row from 18.6 mm to 14.4 mm, and a 14 mm target is one a
 * fingertip misses — on the two rows where a mis-press is a player who believes they are muted
 * and is not.
 *
 * **0.22 m, from 0.16, after the Quest found the list had outgrown it again** (M8-30). At ten rows
 * — `place`, and the root list while initials are being entered — a target was 16.0 mm against the
 * 14 mm this comment already called the miss width, so the panel was running on 2 mm of headroom.
 * Reported from a headset as "too small text/buttons ... so the wrong choices are being made". The
 * type grows with the panel for free, because the raster is a fixed 512 px mapped onto this number.
 */
const PANEL = { width: 0.22, height: 0.22 } as const
const CANVAS = { width: 512, height: 512 } as const

/**
 * How far from the palm toward the eyes the panel sits, metres.
 *
 * **Toward the eyes, not beyond the hand.** The first pose put the mesh six centimetres past the
 * palm along −Z, tipped back — "in front of the wrist, where a watch face would be" — and never
 * turned the group, so it was right for one facing (b1e3d58 turned it). Turned, it was still on
 * the far side of the hand from the player: *"the user is not able to press the buttons because
 * the user's hand is in the way, because the panel is too far away from the user and behind the
 * user's hand."* A watch face is read from the near side. The native client has sat its panel
 * `TowardViewerMetres = 0.07` from the wrist toward the eyes since its own device round trips
 * (25 and 30 Aug 2026: upside down, then rolled with the arm); this is that figure, and the group
 * looks at the head so the face is a billboard with world up as its up — the same "upright to the
 * reader, not aligned to the arm" decision, one client over.
 */
export const TOWARD_VIEWER = 0.07

// The rows a page draws live in `wristMenu.rowsFor`, because the hit test below and the paint at
// the bottom of this file have to agree about what is in row four, and a second copy of that list
// is a second thing to get wrong the next time an item is added (M8-20).

export function buildWristPanel(): WristPanelBuild {
  const group = new Group()
  group.visible = false

  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
  const canvas = host?.createElement('canvas')
  const ctx = canvas?.getContext('2d') ?? null
  if (canvas !== undefined) {
    canvas.width = CANVAS.width
    canvas.height = CANVAS.height
  }

  const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
  if (texture !== undefined) {
    texture.colorSpace = SRGBColorSpace
    // No mipmaps: the panel is viewed at one distance and a mip chain on a texture that is
    // re-uploaded is a re-generated chain each time.
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }

  const geometry = new PlaneGeometry(PANEL.width, PANEL.height)
  const material = new MeshBasicMaterial({
    ...(texture === undefined ? { color: 0x101018 } : { map: texture }),
    transparent: true,
    side: DoubleSide,
    toneMapped: false,
    depthTest: false,
  })
  const mesh = new Mesh(geometry, material)
  // Between the palm and the eye, facing the eye: the group is turned to look at the head each
  // frame (`XRRuntime.drawMenu`), so local +Z is "toward the player" and a PlaneGeometry already
  // faces +Z. See `TOWARD_VIEWER` for why it is on this side of the hand.
  mesh.position.set(0, 0, TOWARD_VIEWER)
  // Drawn last, over everything: a menu occluded by the machine it is used to control is a menu
  // that appears not to have opened.
  mesh.renderOrder = 999
  group.add(mesh)
  // Target feedback never repaints the lettering or uploads its canvas.
  const hoverMaterial = new MeshBasicMaterial({ color: 0x40e0ee, transparent: true, opacity: .18, depthTest: false, depthWrite: false })
  const hover = new Mesh(geometry, hoverMaterial); hover.position.z = TOWARD_VIEWER + .0004; hover.renderOrder = 1000; hover.visible = false; group.add(hover)
  const armMaterial = new MeshBasicMaterial({ color: 0xffc46b, transparent: true, opacity: .85, depthTest: false, depthWrite: false })
  const arm = new Mesh(geometry, armMaterial); arm.position.z = TOWARD_VIEWER + .0005; arm.renderOrder = 1001; arm.visible = false; group.add(arm)

  const contactMaterial=new MeshBasicMaterial({color:0xffdb6b,transparent:true,opacity:1,depthTest:false,depthWrite:false,toneMapped:false})
  const contact=new Mesh(geometry,contactMaterial);contact.name='quick-pointer-contact';contact.scale.setScalar(.025);contact.renderOrder=1013;contact.visible=false;group.add(contact)
  const feedbackMaterial=new MeshBasicMaterial({color:0xffffff,transparent:true,depthTest:false,depthWrite:false,toneMapped:false})
  const feedback=new Mesh(geometry,feedbackMaterial);feedback.name='compact-menu-feedback';feedback.renderOrder=1002;feedback.visible=false
  feedback.position.set((800/1024-.5)*COMPACT_MENU.width,(.5-16/256)*COMPACT_MENU.height,TOWARD_VIEWER+.0003)
  feedback.scale.set(COMPACT_MENU.feedbackWidth/PANEL.width,COMPACT_MENU.feedbackHeight/PANEL.height,1);group.add(feedback)
  const feedbackTextures=new Map<string,CanvasTexture>()
  const showFeedback=(state:MenuState,highlighted:MenuItem|undefined,touch?:'accepted'|'release'):void=>{
    feedback.visible=compact&&group.visible;if(!feedback.visible)return
    const language=state.build?.language??'en'
    let atlas=feedbackTextures.get(language)
    if(atlas===undefined&&host!==undefined){
      const picture=host.createElement('canvas');picture.width=1024;picture.height=512
      const c=picture.getContext('2d')
      if(c!==null){const words=language==='fr'?['Pret','Touchez pour choisir','Reculez la main','Appuye','Terminez la pose','Preparation du test','Preparation du menu','Test en cours']
        :['Menu ready','Push to select','Pull hand back','Touch received','Finish placing first','Preparing test','Preparing menu','Test active']
        const sx=(COMPACT_MENU.feedbackHeight/64)/(COMPACT_MENU.feedbackWidth/1024)
        words.forEach((word,i)=>{c.fillStyle='#111d30';c.fillRect(0,i*64,1024,64);c.save();c.scale(sx,1);c.fillStyle='#e8ecff';c.font='52px system-ui, sans-serif';c.textBaseline='middle';c.fillText(word,20/sx,i*64+32);c.restore()})
        atlas=new CanvasTexture(picture);atlas.colorSpace=SRGBColorSpace;atlas.generateMipmaps=false;atlas.minFilter=LinearFilter;atlas.repeat.set(1,1/8);feedbackTextures.set(language,atlas)
      }
    }
    if(atlas!==undefined){const index=state.build?.unfinishedMove?4:state.build?.testPreparing?5:touch==='accepted'?3:touch==='release'?2:highlighted!==undefined?1:state.build?.testing?7:0
      atlas.offset.set(0,1-(index+1)/8);if(feedbackMaterial.map!==atlas){feedbackMaterial.map=atlas;feedbackMaterial.needsUpdate=true}}
  }
  const physicalPoke=new PhysicalPoke(),outward=new Vector3()
  let lastKey = ''
  /**
   * The page the panel is currently drawn on.
   *
   * Held rather than passed to `itemAt`, because the hit test is called from the hand loop with a
   * fingertip and nothing else — and it has to answer about the rows that are **on the glass**,
   * not the rows the menu has since moved to. They are the same in every frame but the one where
   * the page changed, and that one frame is a press landing on the row that was there a moment ago.
   */
  let page: MenuView['page'] = 'root'
  /** How many machine rows the page has. Zero in mixed reality — one cabinet, no row (M8-20). */
  // Latched in `update` beside `page` and `bayCount`, and for the identical reason: `itemAt` is
  // called from the hand loop with no state in hand, and it has to answer against the same list
  // the last paint drew (M2-19a).
  /**
   * Whether there is a machine in the room (M8-63).
   *
   * Latched beside `page`, `bayCount` and `entering` for the identical reason they are: `itemAt`
   * is called from the hand loop with a fingertip and no state, and it has to answer against the
   * same list the last paint drew.
   */
  /** Whether Build mode is offered (M13-09). Latched beside the four above, for their reason. */
  /** Which mode the wrist can switch to, if any (14 Sep 2026). Latched with the rest, for their reason. */
  let presentation: MenuPresentation = 'normal'
  let currentRows: readonly MenuItem[] = []
  let compact=false,layout=menuLayout(false,0)

  const update = (view: MenuView, state: MenuState, highlighted: MenuItem | undefined, touch?:'accepted'|'release'): void => {
    group.visible = view.open
    // **Before the early return, not after.** A build with no canvas — a test, or a browser that
    // refused a 2-D context — still has to answer `itemAt` correctly, and a panel whose hit test
    // silently stayed on the root page for ever is a machine list nothing can press.
    page = view.page
    presentation = view.presentation ?? 'normal'
    mesh.renderOrder=presentation==='quick'?1010:999;hover.renderOrder=mesh.renderOrder+1;arm.renderOrder=mesh.renderOrder+2
    if(presentation!=='quick')contact.visible=false
    const items = contextRows(view,state)
    const nextCompact=presentation==='normal'&&state.compactMenu===true
    if(compact!==nextCompact){compact=nextCompact;release();lastKey=''}
    layout=menuLayout(compact,items.length,items.at(-1)==='close'&&items.at(-2)==='back');currentRows=items
    if (highlighted !== undefined && unavailableReason(highlighted,state) !== undefined) highlighted = undefined
    showFeedback(state,highlighted,touch);if(view.armed!==undefined)feedback.visible=false
    const physicalWidth=compact?COMPACT_MENU.width:PANEL.width,physicalHeight=compact?COMPACT_MENU.height:PANEL.height
    mesh.scale.set(physicalWidth/PANEL.width,physicalHeight/PANEL.height,1)
    const hoverIndex = highlighted === undefined ? -1 : items.indexOf(highlighted)
    hover.visible=view.open&&hoverIndex>=0
    if(hoverIndex>=0){const c=layout.cell(hoverIndex)
      hover.position.set(((c.x+c.width/2)/layout.width-.5)*physicalWidth,(.5-(c.y+c.height/2)/layout.height)*physicalHeight,TOWARD_VIEWER+.0004)
      hover.scale.set(c.width/layout.width*physicalWidth/PANEL.width,c.height/layout.height*physicalHeight/PANEL.height,1)
    }
    const armIndex=view.armed===undefined?-1:items.indexOf(view.armed)
    arm.visible=view.open&&armIndex>=0&&view.armedFor>0
    if(arm.visible){const c=layout.cell(armIndex),bar=(c.width-40)*Math.max(0,Math.min(1,view.armedFor/3000))
      arm.position.set(((c.x+20+bar/2)/layout.width-.5)*physicalWidth,(.5-(c.y+c.height-4)/layout.height)*physicalHeight,TOWARD_VIEWER+.0005)
      arm.scale.set(bar/layout.width*physicalWidth/PANEL.width,(compact?2:4)/layout.height*physicalHeight/PANEL.height,1)
    }
    if (!view.open || ctx === null || texture === undefined) return

    // **The words this panel would draw — the only thing it can be stale about** (M8-23).
    //
    // This was a hand-picked list of the state fields known to reach a label, and the note that
    // stood here said that was cheaper than diffing seven labels. It was cheaper, and it was wrong
    // three times over. `soundMuted` was missing until M5-34, so muting the machine from the wrist
    // left the row still reading "Sound on". `tabletop` was never added at all, so the scale row
    // goes on saying "Full size" over a machine that has visibly shrunk. `enteringInitials` decides
    // whether there *is* a Skip row, and is not here either. `master` and `captions` would have
    // been the fourth and fifth the moment 06 §5's Sound page landed.
    //
    // Every one of those is the same failure — a field that reaches a label and not this list —
    // and the fix is to stop keeping a second list. Diffing the labels cannot go stale, because
    // the labels *are* the drawing: whatever a future row decides to say, this notices. The cost
    // is building seven short strings on a frame, against a canvas repaint and a texture upload
    // on the frames it saves.
    const key = [
      compact,
      // The page as well, so a page change repaints even in the case where every label matches —
      // a panel that skipped it would leave a player pressing machine rows that are still drawn as
      // Pause and Comfort (M8-20).
      view.page,
      presentation,
      state.currentMachineTitle ?? '',
      activityLabel(state), primaryItem(view,state) ?? '',
      footerFor(view,state),
      state.pendingMenuDetail ?? '',
      ...contextRows(view,state).map((item) => labelFor(item, view.armed === item, state, view.page)+(unavailableReason(item,state)??'')),
      view.armed ?? '',
    ].join('|')
    if (key === lastKey) return
    lastKey = key
    if(canvas!==undefined&&(canvas.width!==layout.width||canvas.height!==layout.height)){canvas.width=layout.width;canvas.height=layout.height}
    paint(ctx, { ...view, armedFor: 0 }, state, undefined)
    texture.needsUpdate = true
  }

  const local = new Vector3()
  /**
   * The row under a point in the panel's own frame, metres from the centre.
   *
   * Local +Y is up and the list is drawn top-down. **In the paint's own coordinates**, so the band
   * a finger lands in is the band the player is looking at — see `TOP_MARGIN`. The clamp keeps
   * the generosity the margins were always meant to have: a finger just above the first row is
   * pointing at the first row, and one below the last is pointing at the last.
   */
  let lastPress: LastPress | undefined
  const rowAt = (localY: number, record = false, localX=0): MenuItem | undefined => {
    const rows = currentRows
    const index=layout.at(localX,localY)
    if(index<0)return undefined
    const cell=layout.cell(index),pixel=(.5-localY/PANEL.height)*layout.height
    if(record)lastPress={row:index,rows:rows.length,down:compact?(pixel-cell.y)/cell.height:(pixel-50-index*428/rows.length)/(428/rows.length)}
    return rows[index]
  }
  /** The row under a point already in the panel's frame, or undefined off the panel. */
  const rowUnder = (l: Vector3): MenuItem | undefined => {
    // A finger has to be near the panel's plane as well as within its edges: a hand resting below
    // the wrist is directly under several rows and is not pointing at any of them.
    if (Math.abs(l.z) > TOUCH_DEPTH) return undefined
    if (Math.abs(l.x) > PANEL.width / 2 || Math.abs(l.y) > PANEL.height / 2) return undefined
    return rowAt(l.y,false,l.x)
  }
  const itemAt = (x: number, y: number, z: number): MenuItem | undefined => {
    if (!group.visible) return undefined
    local.set(x, y, z)
    mesh.worldToLocal(local)
    return rowUnder(local)
  }

  // One gate, one pointing hand: the right index, since the left holds the panel. The face is the
  // quad itself, whose local frame is already the gate's — origin at the centre, +z toward the
  // person — because the group looks at the head (`XRRuntime.drawMenu`).
  const gate = new TapGate({ width: PANEL.width, height: PANEL.height })
  // The row under the finger, since when, and the row it was under before that — the highlight's
  // own history, kept here because the press is decided against it (`COMMIT_SECONDS`).
  let under: MenuItem | undefined
  let underSince = 0
  let underBefore: MenuItem | undefined
  let underBeforeHeld = 0
  let underPage: MenuView['page'] | undefined
  // How near the picture the finger has come while over `under`, and whether it pressed there.
  let underNearest = Number.POSITIVE_INFINITY
  let underPressed = false
  let lastLeave: LastLeave | undefined
  const forget = (): void => {
    under = undefined
    underBefore = undefined
    underPage = undefined
    underNearest = Number.POSITIVE_INFINITY
    underPressed = false
  }
  // The row the last press went to, and whether its finger is still down.
  let pressedItem: MenuItem | undefined
  let fingerDown = false
  const press = (x: number, y: number, z: number, now: number, moving = false): MenuItem | undefined => {
    const item = pressOnce(x, y, z, now, moving)
    if (item !== undefined) pressedItem = item
    if (!fingerDown) pressedItem = undefined
    return item
  }
  const pressOnce = (x: number, y: number, z: number, now: number, moving: boolean): MenuItem | undefined => {
    if (!group.visible) {
      gate.release()
      forget()
      fingerDown = false
      return undefined
    }
    local.set(x, y, z)
    mesh.worldToLocal(local)
    // A page that changed under a still finger is not a finger that moved between rows.
    if (page !== underPage) {forget();physicalPoke.reset()}
    const over = rowUnder(local)
    if (over !== under) {
      if (under !== undefined && !underPressed) {
        const rows = currentRows
        lastLeave = { row: rows.indexOf(under), rows: rows.length, nearest: underNearest }
      }
      underBefore = under
      underBeforeHeld = now - underSince
      under = over
      underSince = now
      underPage = page
      underNearest = Number.POSITIVE_INFINITY
      underPressed = false
    }
    if (under !== undefined) underNearest = Math.min(underNearest, local.z)
    outward.set(0,0,1).transformDirection(mesh.matrixWorld)
    const physical=!moving||physicalPoke.advance(x,y,z,outward.x,outward.y,outward.z,over!==undefined)
    contact.visible=moving&&over!==undefined
    if(contact.visible)contact.position.set(local.x,local.y,TOWARD_VIEWER+.0015)
    const tap = gate.advance({ x: local.x, y: local.y, z: local.z },physical&&(!compact||over!==undefined))
    if(tap.releasedEdge)physicalPoke.reset()
    fingerDown = tap.down
    if (!tap.pressedEdge || tap.at === undefined) return undefined
    underPressed = true
    // The row is decided by where the finger *arrived*, which the gate holds for the press —
    // unless the highlight had only just crossed from a neighbouring row, in which case the
    // finger drifted on the way in and the row that was lit when the person committed is theirs.
    const landed = rowAt(tap.at[1],true,tap.at[0])
    const before = underBefore
    if (before === undefined || landed !== under || before === landed) return landed
    if (now - underSince >= COMMIT_SECONDS || underBeforeHeld < COMMIT_SECONDS) return landed
    const rows = currentRows
    const meant = rows.indexOf(before)
    if (meant < 0 || lastPress === undefined) return landed
    lastPress = { ...lastPress, row: meant, landed: lastPress.row, after: now - underSince }
    return before
  }
  const takeLastPress = (): typeof lastPress => {
    const press = lastPress
    lastPress = undefined
    return press
  }
  const takeLastLeave = (): typeof lastLeave => {
    const leave = lastLeave
    lastLeave = undefined
    return leave
  }
  const release = (): void => {
    gate.release();physicalPoke.reset();contact.visible=false
    forget()
    fingerDown = false
    pressedItem = undefined
  }
  const held = (): MenuItem | undefined => (fingerDown ? pressedItem : undefined)

  return {
    group,
    itemAt,
    press,
    held,
    takeLastPress,
    takeLastLeave,
    release,
    geometries: [geometry],
    materials: [material, hoverMaterial, armMaterial, contactMaterial,feedbackMaterial],
    get textures(){return [...(texture===undefined?[]:[texture]),...feedbackTextures.values()]},
    update,
    dispose: () => {
      geometry.dispose()
      material.dispose()
      hoverMaterial.dispose(); armMaterial.dispose(); contactMaterial.dispose();feedbackMaterial.dispose();for(const atlas of feedbackTextures.values())atlas.dispose()
      texture?.dispose()
    },
  }
}

/** Compact changes presentation only; full history and occupant names remain in state/refusal details. */
function compactLabel(item:MenuItem,state:MenuState,page:MenuView['page']):string {
  if(item==='undo'&&state.build?.canUndo)return state.build.language==='fr'?'Annuler la dernière action':'Undo last edit'
  if(item==='redo'&&state.build?.canRedo)return state.build.language==='fr'?'Rétablir la dernière action':'Redo last edit'
  const label=labelFor(item,false,state,page),at=label.indexOf(' · ')
  if(/^bay[1-6]$/.test(item)&&at>=0&&!['current','you'].includes(label.slice(at+3)))return label.slice(0,at)+' · occupied'
  return label
}

function paint(
  ctx: CanvasRenderingContext2D,
  view: MenuView,
  state: MenuState,
  highlighted: MenuItem | undefined,
): void {
  const compact=(view.presentation??'normal')==='normal'&&state.compactMenu===true
  const rows=contextRows(view,state),layout=menuLayout(compact,rows.length,rows.at(-1)==='close'&&rows.at(-2)==='back')
  const {width,height}=layout
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#0b1219'
  roundedRect(ctx,4,4,width-8,height-8,compact?10:22)
  ctx.fill()
  ctx.strokeStyle = '#3a4c57'
  ctx.lineWidth = 3
  ctx.stroke()

  ctx.textBaseline = 'middle'
  ctx.fillStyle='#8edbd8';ctx.font=`600 ${compact?22:16}px system-ui, sans-serif`
  if(compact&&view.armed!==undefined){ctx.fillStyle='#ffd594';ctx.font='600 30px system-ui, sans-serif';ctx.fillText(labelFor(view.armed,true,state),24,16)}
  else if(compact){let heading=headerFor(view)+(state.currentMachineTitle?' · '+state.currentMachineTitle:'')
    while(ctx.measureText(heading).width>552&&heading.length>4)heading=heading.slice(0,-4)+'...'
    ctx.fillText(heading,24,16)
  }else{
    ctx.fillText(headerFor(view),24,20,width-48)
    ctx.fillStyle='#b5c4ce';ctx.font='15px system-ui, sans-serif'
    ctx.fillText(`${activityLabel(state)}${state.currentMachineTitle?' · '+state.currentMachineTitle:''}`,24,39,width-48)
  }
  ctx.font = `600 ${compact?30:27}px system-ui, sans-serif`

  rows.forEach((item, i) => {
    const c=layout.cell(i),y=c.y+c.height/2
    const available=unavailableReason(item,state)===undefined
    const armed = available && view.armed === item
    const primary = available && item === primaryItem(view,state)
    if (compact || available && (highlighted === item || armed || primary)) {
      ctx.fillStyle = armed ? 'rgba(255, 196, 107, 0.22)' : primary ? highlighted === item ? '#b3eee6' : '#8edbd8' : highlighted===item?'rgba(142, 219, 216, 0.16)':compact?'#111f2a':'rgba(142, 219, 216, 0.16)'
      roundedRect(ctx,c.x,c.y,c.width,c.height,compact?6:12)
      ctx.fill()
    }
    // An armed item is amber and says what the *second* press will do — the label, not a colour,
    // is what carries the warning, because a colour is not a sentence.
    // A muted row is drawn in the warning colour as well as saying so. Not *instead* of saying
    // so — a colour is not a sentence, which is the same rule the armed rows follow — but a
    // player glancing at their wrist mid-ball reads the colour first.
    // The machine's own two joined the room's two with 06 §5's Sound page. `muteAll` is the row
    // `sound` used to be; `masterVolume` at `off` is the second way to arrive at a machine making
    // no noise, and a silence that only the *words* reported would be the one on this panel a
    // glance could miss.
    const muted =
      (item === 'muteMic' && state.micMuted) ||
      (item === 'muteVoice' && state.voiceMuted) ||
      (item === 'muteAll' && state.soundMuted) ||
      (item === 'masterVolume' && state.master === 'off')
    ctx.fillStyle = unavailableReason(item,state)!==undefined ? '#7d8992' : armed ? '#ffd594' : primary ? '#0b1219' : muted ? '#ffc676' : '#f2f0e7'
    const label=compact?compactLabel(item,state,view.page):labelFor(item,armed,state,view.page)
    if(compact){let line='',lines:string[]=[]
      for(const word of label.split(' ')){const next=line?line+' '+word:word;if(line&&ctx.measureText(next).width>c.width-48){lines.push(line);line=word}else line=next}
      if(line)lines.push(line)
      lines.forEach((value,index)=>ctx.fillText(value,c.x+16,y+(index-(lines.length-1)/2)*24))
    }else ctx.fillText(label,36,y,width-96)
    if (item !== 'back' && (PAGING.has(item) || item === 'otherMachine' || item === 'build' || item === 'fullMenu' || item === 'currentRules' || item === 'account')) {
      ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 2.5
      const right=compact?c.x+c.width-16:width-40;ctx.beginPath();ctx.moveTo(right-7,y-6);ctx.lineTo(right,y);ctx.lineTo(right-7,y+6);ctx.stroke()
    }

    if (!armed || view.armedFor <= 0) return
    // A countdown bar rather than a number: the player is looking at their wrist, mid-game.
    const fraction = Math.max(0, Math.min(1, view.armedFor / 3000))
    ctx.fillStyle = 'rgba(255, 196, 107, 0.85)'
    ctx.fillRect(c.x+20,c.y+c.height-5,(c.width-40)*fraction,compact?2:4)
  })
  ctx.fillStyle = state.pendingMenuDetail === undefined ? '#9aafbd' : '#ffd594'
  ctx.font = `${compact?12:14}px system-ui, sans-serif`
  ctx.fillText((compact?activityLabel(state)+' · ':'')+footerFor(view,state),24,compact?247:height-15,width-48)
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}
